import React, { useEffect, useState, useCallback } from 'react';
import { Monitor, Projector, Smartphone, X, Sparkles, Loader2, Check, AlertCircle, Rocket, Users } from 'lucide-react';

/**
 * Wizard "Lancer RVP en 1 clic" — affiché au démarrage d'une session RVP
 * si l'enseignant ne l'a pas désactivé pour la session.
 *
 * Props :
 *   - open (bool) — visible ?
 *   - onClose ()
 *   - onLaunchProjector () — lance le mode vidéoprojecteur (2 écrans)
 *   - onLaunchSolo () — mode solo (juste la présentation, pas de vitrine)
 *   - onLaunchCast () — mode télécommande téléphone → écran distant
 *   - slug (string)
 *   - rvpTitle (string)
 */
export default function RvpLaunchWizard({ open, onClose, onLaunchProjector, onLaunchSolo, onLaunchCast, onLaunchSpectateur, slug, rvpTitle }) {
  const [screenDetected, setScreenDetected] = useState(null); // null | true | false
  const [checking, setChecking] = useState(true);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Détection écran secondaire au montage
  useEffect(() => {
    if (!open) return;
    setChecking(true);
    setScreenDetected(null);
    const timer = setTimeout(async () => {
      try {
        if (typeof window.getScreenDetails === 'function') {
          const details = await window.getScreenDetails();
          const screens = details.screens || [];
          setScreenDetected(screens.length >= 2);
        } else {
          setScreenDetected(false);
        }
      } catch {
        setScreenDetected(false);
      }
      setChecking(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [open]);

  const persistDontShow = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(`rvp-skip-launch-wizard-${slug}`, '1');
    }
  }, [dontShowAgain, slug]);

  const handleProjector = useCallback(async () => {
    setLaunching(true);
    persistDontShow();
    await onLaunchProjector();
    setLaunching(false);
    onClose();
  }, [onLaunchProjector, onClose, persistDontShow]);

  const handleSolo = useCallback(() => {
    persistDontShow();
    if (onLaunchSolo) onLaunchSolo();
    onClose();
  }, [onLaunchSolo, onClose, persistDontShow]);

  const handleCast = useCallback(() => {
    persistDontShow();
    if (onLaunchCast) onLaunchCast();
    onClose();
  }, [onLaunchCast, onClose, persistDontShow]);

  const handleSpectateur = useCallback(() => {
    persistDontShow();
    if (onLaunchSpectateur) onLaunchSpectateur();
    onClose();
  }, [onLaunchSpectateur, onClose, persistDontShow]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="launch-wizard">
      <div
        className="w-full max-w-2xl rounded-3xl border border-red-500/40 bg-gradient-to-br from-[#160a0c] via-[#0d0d0e] to-[#0a0a0b] p-6 sm:p-9 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Rocket size={11} /> Lancement rapide
          </div>
          <button onClick={onClose} data-testid="launch-wizard-close" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        <h2 className="font-serif-editorial text-white text-[28px] sm:text-[36px] leading-[1.05] mb-2">
          Comment veux-tu animer ce RVP ?
        </h2>
        <p className="text-white/55 text-[13.5px] sm:text-[14.5px] leading-relaxed mb-6">
          {rvpTitle || slug} · Choisis ton dispositif — je m&apos;occupe du reste.
        </p>

        {/* Détection écran */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-5 text-[12.5px] inline-flex items-center gap-2">
          {checking ? (
            <><Loader2 size={13} className="animate-spin text-white/50" /> <span className="text-white/50">Détection des écrans branchés…</span></>
          ) : screenDetected === true ? (
            <><Check size={13} className="text-emerald-400" /> <span className="text-emerald-300">Écran secondaire détecté</span> <span className="text-white/40">— tout est prêt !</span></>
          ) : screenDetected === false ? (
            <><AlertCircle size={13} className="text-amber-400" /> <span className="text-amber-300">Aucun écran secondaire détecté</span> <span className="text-white/40">— tu peux quand même lancer</span></>
          ) : null}
        </div>

        {/* 3 options de lancement */}
        <div className="grid gap-3">
          {/* Option 1 — Vidéoprojecteur en salle */}
          <button
            onClick={handleProjector}
            disabled={launching}
            data-testid="launch-projector"
            className={`text-left rounded-2xl border-2 p-5 transition group disabled:opacity-50 ${
              screenDetected
                ? 'border-red-500/60 bg-gradient-to-br from-red-950/30 to-red-950/10 hover:border-red-400 hover:from-red-950/50'
                : 'border-white/15 bg-white/[0.03] hover:border-white/30'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${screenDetected ? 'bg-red-500/25 text-red-200 border border-red-500/50' : 'bg-white/5 text-white/60 border border-white/10'}`}>
                <Projector size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-white font-serif-editorial text-[19px] sm:text-[22px] leading-tight">Cours en salle avec vidéoprojecteur</div>
                  {screenDetected && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-bold uppercase tracking-widest border border-emerald-500/40"><Sparkles size={9} /> 1 clic</span>}
                </div>
                <div className="text-white/60 text-[13px] mt-1.5 leading-relaxed">
                  {screenDetected
                    ? 'La vitrine s\u2019ouvre AUTOMATIQUEMENT sur ton écran secondaire, en plein écran. Ton écran principal reste ton contrôle.'
                    : 'Ouvre une 2ème fenêtre pour le projecteur. Si tu n\u2019as pas branché l\u2019écran secondaire, elle apparaîtra à côté — glisse-la manuellement.'}
                </div>
              </div>
            </div>
            {launching && <div className="mt-3 text-[12px] text-white/70 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Ouverture en cours…</div>}
          </button>

          {/* Option 2 — Télécommande téléphone (cross-device) */}
          {onLaunchCast && (
            <button
              onClick={handleCast}
              data-testid="launch-cast"
              className="text-left rounded-2xl border-2 border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-950/30 to-[#0d0d0e] hover:border-fuchsia-400 p-5 transition"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/50 flex items-center justify-center shrink-0">
                  <Smartphone size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-serif-editorial text-[19px] sm:text-[22px] leading-tight">Téléphone en télécommande</div>
                  <div className="text-white/60 text-[13px] mt-1.5 leading-relaxed">
                    Diffuse le cours vers un écran distant (Smart TV, PC HDMI, tablette de salle) via un code + QR. Ton téléphone contrôle et sert de pointeur laser.
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* Option 3 — Spectateur QR (élèves suivent depuis leur téléphone) */}
          {onLaunchSpectateur && (
            <button
              onClick={handleSpectateur}
              data-testid="launch-spectateur"
              className="text-left rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-950/30 to-[#0d0d0e] hover:border-emerald-400 p-5 transition"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-200 border border-emerald-500/50 flex items-center justify-center shrink-0">
                  <Users size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-serif-editorial text-[19px] sm:text-[22px] leading-tight">Spectateur QR pour les élèves</div>
                  <div className="text-white/60 text-[13px] mt-1.5 leading-relaxed">
                    Un QR affiché à la classe : les élèves scannent et voient le cours en direct sur leur téléphone. Idéal pour ceux mal placés, malvoyants, ou pour la révision post-séance.
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* Option 3 — Solo */}
          <button
            onClick={handleSolo}
            data-testid="launch-solo"
            className="text-left rounded-2xl border-2 border-white/10 bg-white/[0.02] hover:border-white/25 p-5 transition"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/5 text-white/70 border border-white/10 flex items-center justify-center shrink-0">
                <Monitor size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-serif-editorial text-[19px] sm:text-[22px] leading-tight">Mode solo · Un seul écran</div>
                <div className="text-white/60 text-[13px] mt-1.5 leading-relaxed">
                  Pas de vidéoprojecteur pour l&apos;instant. Je te laisse animer avec juste ta présentation à l&apos;écran (tu peux activer la vitrine plus tard).
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Ne plus demander */}
        <label className="mt-6 flex items-center gap-2.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            data-testid="launch-dont-show"
            className="w-4 h-4 rounded border-white/20 bg-white/5 accent-red-500"
          />
          <span className="text-white/60 group-hover:text-white/80 text-[12.5px]">
            Ne plus me proposer ce choix pour ce RVP (je pourrai toujours changer depuis la topbar)
          </span>
        </label>
      </div>
    </div>
  );
}
